<script lang="ts" setup>
  import type { Component as ComponentType } from 'vue';
  import { computed, ref, watchEffect } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import TabItem from './TabItem/TabItem.vue';
  import TabSpacer from './TabSpacer/TabSpacer.vue';
  import { tabsNavigationWrapperClass, tabsRowClass } from './Tabs.css.ts';

  const route = useRoute();
  const router = useRouter();

  type TabsProps = {
    firstSelectedTab: string;
    tabs: {
      name: string;
      label: string;
      onClick?: () => void;
      component: ComponentType;
      props?: Record<string, unknown>;
    }[];
  };

  const props = defineProps<TabsProps>();

  defineOptions({
    inheritAttrs: false,
  });

  const selectedTabName = ref<string>(
    (route.query.tab as string) || props.firstSelectedTab,
  );

  watchEffect(() => {
    if (route.query.tab) {
      selectedTabName.value = route.query.tab as string;
    } else {
      selectedTabName.value = props.firstSelectedTab;
    }
  });

  const selectedTab = computed(() => {
    const foundTab = props.tabs.find(
      (tab) => tab.name === selectedTabName.value,
    );
    return foundTab;
  });

  const onSelectTab = (tabName: string, onClick?: () => void) => {
    selectedTabName.value = tabName;
    router.push({ query: { ...route.query, tab: tabName } });
    if (onClick) {
      onClick();
    }
  };

  const getComponentByTabName = (tabName: string) => {
    const tab = props.tabs.find((tab) => tab.name === tabName);
    return tab?.component;
  };
</script>

<template>
  <div>
    <div :class="tabsNavigationWrapperClass">
      <div v-for="(tab, index) in tabs" :key="tab.name" :class="tabsRowClass">
        <TabItem
          :label="tab.label"
          :name="tab.name"
          :is-first="index === 0"
          :is-last="index === tabs.length - 1"
          @click="() => onSelectTab(tab.name, tab.onClick)"
          :selected="tab.name === selectedTabName"
        />
        <TabSpacer
          v-if="index !== tabs.length - 1"
          :selected="tab.name === selectedTabName"
        />
      </div>
    </div>
    <component
      :is="getComponentByTabName(selectedTabName)"
      v-bind="selectedTab?.props"
    />
  </div>
</template>
